// Brokerage Certification & Pipeline — the soft checklist behind a stage click
// on the BrokerageStageStepper (mirrors the broker StageAdvancePanel). One
// stage-readiness GET drives auto-evaluated rows for the target transition; rows
// are INFORMATIONAL ONLY (met = gold check, unmet = muted circle, never red) and
// NEVER block the move — the lifecycle stays fully manual. A backward move shows
// a reassurance note instead of a checklist. The footer posts the manual
// status-change endpoint (Brokerages.Update, optional reason).

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Info } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  Label,
  SubmitButton,
  Textarea,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import {
  brokeragesApi,
  BROKERAGE_PIPELINE_STATUSES,
  type BrokerageStageReadinessDto,
  type BrokerageStatus,
} from "@/api/brokerages";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";

export interface ChecklistRow {
  label: string;
  /** undefined = static info row (bullet, no met/unmet state). */
  met?: boolean;
}

// The only stage whose display label differs from its enum name.
const stageLabel = (s: BrokerageStatus) => (s === "UnderReview" ? "Under review" : s);

/** Builds the soft-checklist content for a forward transition. */
export function buildBrokerageStageChecklist(
  target: BrokerageStatus,
  r: BrokerageStageReadinessDto,
): ChecklistRow[] {
  const gates = r.stageGates
    .filter((g) => g.stageTo === target)
    .map((g) => ({ label: g.label, met: g.certified }));

  switch (target) {
    case "Applied":
      return [
        { label: "Contact details on file", met: r.hasContactDetails },
        ...gates,
        { label: "Firm application received" },
      ];
    case "UnderReview":
      return [
        { label: "Contact details on file", met: r.hasContactDetails },
        { label: "Firm address on file", met: r.hasAddress },
        { label: "W-9 / TIN captured", met: r.hasTaxId },
        { label: "Portal account provisioned", met: r.portalAccountProvisioned },
        { label: "Active sub-brokers on roster", met: r.hasActiveSubBrokers },
        ...gates,
        { label: "Application documents submitted for review" },
      ];
    case "Certified":
      return [
        ...gates,
        { label: "Certification review completed with the Broker Manager (C0)" },
      ];
    default:
      // →Prospect is always a backward move; no checklist content exists.
      return [];
  }
}

export function BrokerageStageAdvancePanel({
  brokerage,
  target,
  onClose,
  onChanged,
}: {
  brokerage: { id: string; name: string; status: BrokerageStatus };
  target: BrokerageStatus;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");

  const currentIndex = BROKERAGE_PIPELINE_STATUSES.indexOf(brokerage.status);
  const targetIndex = BROKERAGE_PIPELINE_STATUSES.indexOf(target);
  const backward = targetIndex < currentIndex;
  const targetLabel = stageLabel(target);

  const readiness = useQuery({
    queryKey: brokerageKeys.stageReadiness(brokerage.id),
    queryFn: () => brokeragesApi.stageReadiness(brokerage.id),
    enabled: !backward,
  });

  const move = useSubmittingMutation(
    () => brokeragesApi.changeStatus(brokerage.id, target, reason.trim() || undefined),
    {
      onSuccess: () => {
        toast.success(`Moved to ${targetLabel}.`);
        // Legacy singular detail key (header) + `all` (list + pipeline board) +
        // the readiness key this panel reads.
        void qc.invalidateQueries({ queryKey: brokerageKeys.legacyDetail(brokerage.id) });
        void qc.invalidateQueries({ queryKey: brokerageKeys.all });
        void qc.invalidateQueries({ queryKey: brokerageKeys.stageReadiness(brokerage.id) });
        onChanged();
        onClose();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const rows = readiness.data ? buildBrokerageStageChecklist(target, readiness.data) : [];

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Move to ${targetLabel}`}
      description={`${brokerage.name} — currently ${brokerage.status}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={move.pending}>
            Cancel
          </Button>
          {/* The checklist is advisory — this button is never disabled by
              checklist state, only while the POST is in flight. */}
          <SubmitButton
            pending={move.pending}
            pendingLabel="Moving…"
            onClick={() => void move.submit()}
            data-testid="brokerage-stage-advance-confirm"
          >
            Move to {targetLabel}
          </SubmitButton>
        </>
      }
    >
      {backward ? (
        <p className="text-small text-gray-dark" data-testid="brokerage-stage-backward-note">
          Moving {brokerage.name} back to {targetLabel}. This doesn&apos;t undo any
          recorded certifications.
        </p>
      ) : (
        <>
          <p className="text-caption text-gray-mid mb-space-2">Readiness Checklist:</p>
          {readiness.isLoading && (
            <div className="text-caption text-gray-mid">Loading readiness…</div>
          )}
          {readiness.isError && (
            <div className="text-caption text-gray-mid italic">
              Readiness could not be loaded — you can still move the firm.
            </div>
          )}
          {readiness.data && (
            <ul className="m-0 list-none p-0 space-y-space-2" data-testid="brokerage-stage-checklist">
              {rows.map((row) => (
                <li key={row.label} className="flex items-start gap-space-2 text-small">
                  {row.met === undefined ? (
                    <Info size={14} className="mt-[2px] shrink-0 text-status-info" aria-hidden />
                  ) : row.met ? (
                    <CheckCircle2
                      size={14}
                      className="mt-[2px] shrink-0 text-gold-shadow"
                      aria-hidden
                      data-checklist-state="met"
                    />
                  ) : (
                    <Circle
                      size={14}
                      className="mt-[2px] shrink-0 text-gray-mid"
                      aria-hidden
                      data-checklist-state="unmet"
                    />
                  )}
                  <span className={row.met === false ? "text-gray-mid" : undefined}>{row.label}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className="mt-space-3">
        <Label className="mb-space-1 block">Reason / notes (optional)</Label>
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional note for the audit trail."
        />
      </div>
    </Modal>
  );
}
